import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import {
  addStudioPhotographer,
  closeStudio,
  createStudio,
  loadStudioMembers,
  removeStudioMember,
  updateStudio,
} from "./api";
import "./studio-management.css";

const emptyForm = {
  name: "",
  ownerPhotographerId: "",
  email: "",
  phone: "",
  address: "",
  timezone: "Asia/Kolkata",
  defaultRetentionDays: "90",
};

function photographerId(profile) {
  return profile.user?.id || profile.userId || profile.id;
}

function photographerName(profile) {
  return (
    profile.user?.displayName ||
    profile.displayName ||
    profile.businessName ||
    photographerId(profile)
  );
}

export function StudioManagement({
  token,
  studios = [],
  photographers = [],
  refresh,
  notify,
}) {
  const [selectedId, setSelectedId] = useState(studios[0]?.id || "");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const selected = studios.find((studio) => studio.id === selectedId);
  const approved = useMemo(
    () =>
      photographers.filter(
        (profile) =>
          (profile.status === "approved" ||
            profile.profileStatus === "approved") &&
          profile.user?.status !== "suspended" &&
          profile.user?.status !== "disabled",
      ),
    [photographers],
  );
  const memberUserIds = new Set(
    members
      .filter((member) => member.status !== "revoked")
      .map((member) => member.user?.id),
  );
  const availableMembers = approved.filter(
    (profile) => !memberUserIds.has(photographerId(profile)),
  );

  useEffect(() => {
    if (!studios.some((studio) => studio.id === selectedId)) {
      setSelectedId(studios[0]?.id || "");
    }
  }, [selectedId, studios]);

  useEffect(() => {
    let active = true;
    setMembers([]);
    setMemberId("");
    if (!selectedId) return undefined;
    loadStudioMembers(token, selectedId)
      .then((records) => active && setMembers(records))
      .catch((requestError) => active && setError(requestError.message));
    return () => {
      active = false;
    };
  }, [selectedId, token]);

  function beginCreate() {
    setEditing("create");
    setForm(emptyForm);
    setError("");
  }

  function beginEdit() {
    if (!selected) return;
    setEditing("edit");
    setForm({
      ...emptyForm,
      name: selected.name || "",
      email: selected.contact?.email || "",
      phone: selected.contact?.phone || "",
      address: selected.contact?.address || "",
      timezone: selected.settings?.timezone || "Asia/Kolkata",
      defaultRetentionDays: String(
        selected.settings?.defaultRetentionDays || 90,
      ),
    });
    setError("");
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const values = {
      name: form.name.trim(),
      contact: {
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.address.trim() ? { address: form.address.trim() } : {}),
      },
      settings: {
        timezone: form.timezone.trim(),
        defaultRetentionDays: Number(form.defaultRetentionDays),
      },
    };
    try {
      if (editing === "create") {
        const created = await createStudio(token, {
          ...values,
          ownerPhotographerId: form.ownerPhotographerId,
        });
        setSelectedId(created.id);
        notify?.(`${created.name} created`);
      } else {
        await updateStudio(token, selected.id, values);
        notify?.(`${values.name} updated`);
      }
      setEditing(false);
      await refresh?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeStudio() {
    if (
      !selected ||
      !globalThis.confirm(
        `Close ${selected.name}? This removes it from future bookings.`,
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await closeStudio(token, selected.id);
      notify?.(`${selected.name} closed`);
      setSelectedId("");
      await refresh?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function addMember(event) {
    event.preventDefault();
    if (!selected || !memberId) return;
    setBusy(true);
    setError("");
    try {
      await addStudioPhotographer(token, selected.id, memberId);
      setMembers(await loadStudioMembers(token, selected.id));
      setMemberId("");
      notify?.("Photographer added to the studio team");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member) {
    if (!selected || member.role === "owner") return;
    setBusy(true);
    setError("");
    try {
      await removeStudioMember(token, selected.id, member.id);
      setMembers(await loadStudioMembers(token, selected.id));
      notify?.("Studio membership revoked");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="studio-management">
      <header className="studio-heading">
        <div>
          <p className="eyebrow">Partner organizations</p>
          <h1>Studios & teams</h1>
          <p>
            Studios belong to approved photographer businesses. Admin manages
            the directory and team membership; clients never create studios.
          </p>
        </div>
        <div className="studio-heading-actions">
          <button
            className="button button-light"
            onClick={() => refresh?.()}
            disabled={busy}
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            className="button button-dark"
            onClick={beginCreate}
            disabled={busy || !approved.length}
          >
            <Plus size={15} /> New studio
          </button>
        </div>
      </header>
      {error && <div className="admin-alert">{error}</div>}
      {!approved.length && (
        <div className="admin-alert">
          Approve at least one photographer before creating a studio.
        </div>
      )}
      <div className="studio-layout">
        <aside className="studio-list">
          {studios.length ? (
            studios.map((studio) => (
              <button
                key={studio.id}
                className={studio.id === selectedId ? "active" : ""}
                onClick={() => setSelectedId(studio.id)}
              >
                <Building2 size={18} />
                <span>
                  <strong>{studio.name}</strong>
                  <small>
                    {studio.owner?.displayName || "Owner unavailable"}
                  </small>
                </span>
              </button>
            ))
          ) : (
            <p>No studios yet. Create one for an approved photographer.</p>
          )}
        </aside>
        <article className="studio-detail">
          {selected ? (
            <>
              <header>
                <div>
                  <small>Active studio</small>
                  <h2>{selected.name}</h2>
                  <p>{selected.contact?.address || "No address supplied"}</p>
                </div>
                <div className="studio-heading-actions">
                  <button className="button button-light" onClick={beginEdit}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    className="button studio-danger"
                    onClick={removeStudio}
                  >
                    <Trash2 size={14} /> Close
                  </button>
                </div>
              </header>
              <div className="studio-facts">
                <span>
                  <small>Owner</small>
                  <strong>{selected.owner?.displayName || "Unknown"}</strong>
                </span>
                <span>
                  <small>Email</small>
                  <strong>
                    {selected.contact?.email ||
                      selected.owner?.email ||
                      "Not supplied"}
                  </strong>
                </span>
                <span>
                  <small>Timezone</small>
                  <strong>
                    {selected.settings?.timezone || "Asia/Kolkata"}
                  </strong>
                </span>
              </div>
              <h3>Studio team</h3>
              <div className="studio-members">
                {members.map((member) => (
                  <div key={member.id}>
                    <span>
                      <strong>
                        {member.user?.displayName || member.user?.email}
                      </strong>
                      <small>
                        {member.role} · {member.status}
                      </small>
                    </span>
                    {member.role !== "owner" && member.status !== "revoked" && (
                      <button
                        aria-label={`Remove ${member.user?.displayName || "member"}`}
                        onClick={() => removeMember(member)}
                        disabled={busy}
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <form className="studio-add-member" onSubmit={addMember}>
                <select
                  value={memberId}
                  onChange={(event) => setMemberId(event.target.value)}
                  disabled={!availableMembers.length}
                >
                  <option value="">
                    {availableMembers.length
                      ? "Select approved photographer"
                      : "No photographers available"}
                  </option>
                  {availableMembers.map((profile) => (
                    <option
                      key={photographerId(profile)}
                      value={photographerId(profile)}
                    >
                      {photographerName(profile)}
                    </option>
                  ))}
                </select>
                <button
                  className="button button-dark"
                  disabled={busy || !memberId}
                >
                  <UserPlus size={15} /> Add member
                </button>
              </form>
            </>
          ) : (
            <div className="empty-state">Select or create a studio.</div>
          )}
        </article>
      </div>
      {editing && (
        <div className="admin-modal" role="dialog" aria-modal="true">
          <form className="catalogue-editor studio-editor" onSubmit={submit}>
            <button
              type="button"
              className="qr-close"
              aria-label="Close studio editor"
              onClick={() => setEditing(false)}
            >
              <X size={20} />
            </button>
            <p className="eyebrow">
              {editing === "create"
                ? "Create organization"
                : "Update organization"}
            </p>
            <h2>
              {editing === "create"
                ? "New photographer studio"
                : `Edit ${selected?.name}`}
            </h2>
            <label>
              Studio name *
              <input
                required
                minLength="2"
                maxLength="160"
                value={form.name}
                onChange={(event) =>
                  setForm({ ...form, name: event.target.value })
                }
              />
            </label>
            {editing === "create" && (
              <label>
                Photographer owner *
                <select
                  required
                  value={form.ownerPhotographerId}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      ownerPhotographerId: event.target.value,
                    })
                  }
                >
                  <option value="">Select approved photographer</option>
                  {approved.map((profile) => (
                    <option
                      key={photographerId(profile)}
                      value={photographerId(profile)}
                    >
                      {photographerName(profile)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="catalogue-form-grid">
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    setForm({ ...form, email: event.target.value })
                  }
                />
              </label>
              <label>
                Phone
                <input
                  minLength="7"
                  value={form.phone}
                  onChange={(event) =>
                    setForm({ ...form, phone: event.target.value })
                  }
                />
              </label>
              <label className="wide">
                Address
                <input
                  value={form.address}
                  onChange={(event) =>
                    setForm({ ...form, address: event.target.value })
                  }
                />
              </label>
              <label>
                Timezone
                <input
                  required
                  value={form.timezone}
                  onChange={(event) =>
                    setForm({ ...form, timezone: event.target.value })
                  }
                />
              </label>
              <label>
                Default retention days
                <input
                  required
                  type="number"
                  min="1"
                  max="3650"
                  value={form.defaultRetentionDays}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      defaultRetentionDays: event.target.value,
                    })
                  }
                />
              </label>
            </div>
            <button className="button button-dark" disabled={busy}>
              {busy ? "Saving…" : "Save studio"}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
